import { onClearAllStores } from '@/modules/auth/stores/AuthStore'
import ApiClient, { FILE_TIMEOUT } from '@/util/ApiClient'
import { resetStaticFirstRenderRollout, setStaticFirstRenderRollout } from '@/util/feature-flags'
import { defineStore } from 'pinia'
import Team from '../models/Team'
import Subscription, {
  AccessState,
  LifecycleState,
  SubscriptionEntitlements,
  SubscriptionPlan
} from '../models/Subscription'
import { DomainResponse, WhitelistedDomain } from '../models/Domain'
import {
  TeamEmailConfig,
  TeamEmailConfigGetResponse,
  TeamEmailConfigRequest,
  TeamEmailConfigSaveResponse
} from '../models/TeamEmailConfig'

/** Ensures API-backed fields match app expectations (boolean for enableDigitalSignature). */
function applyTeamApiNormalization(team: Team): Team {
  team.enableDigitalSignature = !!team.enableDigitalSignature
  return team
}

/**
 * NS-205: fold `GET /subscriptions/entitlements` into the subscription `GET /teams/current` served.
 *
 * The team endpoint serializes the Subscription *entity*, which has no `isTrialLocked`,
 * `isTrialActive`, `permissions`, `daysRemaining` or `warningLevel` — the BE computes those only for
 * the entitlement DTO. Read off the team payload alone, every one of them is `undefined`, so a
 * soft-locked team is indistinguishable from a healthy FREE team: no soft-lock bar, and
 * `FeatureRestrictionStore.hasPermission` degrading to "allow everything".
 *
 * `planCode` is the *effective* catalog plan and the only place TRIAL_EXPIRED ever appears; the
 * entity's `plan` enum column keeps its pre-trial value (usually FREE) for the row's whole life.
 */
export function applyEntitlements(subscription: Subscription, entitlements: SubscriptionEntitlements): Subscription {
  return {
    ...subscription,
    plan: entitlements.planCode ?? subscription.plan,
    trialPlanCode: entitlements.trialPlanCode ?? subscription.trialPlanCode,
    lifecycleState: entitlements.lifecycleState ?? subscription.lifecycleState,
    accessState: entitlements.accessState ?? subscription.accessState,
    isTrialActive: entitlements.isTrialActive ?? false,
    isTrialLocked: entitlements.isTrialLocked ?? false,
    permissions: entitlements.permissions ?? [],
    // null is meaningful here (unlimited), so it is carried through rather than defaulted.
    maxUsers: entitlements.maxUsers ?? null,
    daysRemaining: entitlements.daysRemaining ?? 0,
    warningLevel: entitlements.warningLevel ?? null,
    staticFirstRender: entitlements.staticFirstRender ?? subscription.staticFirstRender
  }
}

function syncStaticFirstRenderRollout(team: Team, entitlements: SubscriptionEntitlements | null): void {
  setStaticFirstRenderRollout({
    teamId: team.id,
    backendFlag: entitlements?.staticFirstRender ?? team.subscription?.staticFirstRender
  })
}

let rolloutClearHookRegistered = false
function ensureRolloutClearHook(): void {
  if (rolloutClearHookRegistered) return
  rolloutClearHookRegistered = true
  onClearAllStores(resetStaticFirstRenderRollout)
}

/**
 * Team writes (rename, logo, banner) answer with a freshly serialized entity, which drops the
 * entitlement fields merged above. Carrying them over keeps the soft-lock bar and the permission
 * gating in place instead of quietly unlocking the team until the next boot.
 */
function preserveEntitlements(team: Team, previous: Team | null): Team {
  const previousSubscription = previous?.subscription
  if (!team.subscription || !previousSubscription || previousSubscription.id !== team.subscription.id) {
    return team
  }
  team.subscription = applyEntitlements(team.subscription, {
    lifecycleState: previousSubscription.lifecycleState,
    accessState: previousSubscription.accessState,
    isTrialActive: previousSubscription.isTrialActive,
    isTrialLocked: previousSubscription.isTrialLocked,
    trialPlanCode: previousSubscription.trialPlanCode ?? null,
    planCode: previousSubscription.plan,
    permissions: previousSubscription.permissions,
    maxUsers: previousSubscription.maxUsers ?? null,
    daysRemaining: previousSubscription.daysRemaining,
    warningLevel: previousSubscription.warningLevel,
    staticFirstRender: previousSubscription.staticFirstRender
  } as SubscriptionEntitlements)
  return team
}

/**
 * Soft lock, resilient to a missing entitlement payload: `accessState` ships on the team endpoint
 * too, so the bar and the gating still engage when `/subscriptions/entitlements` failed or has not
 * been merged in yet.
 */
export function isSoftLockedSubscription(subscription?: Subscription | null): boolean {
  if (!subscription) {
    return false
  }
  return subscription.isTrialLocked === true || subscription.accessState === AccessState.SOFT_LOCKED
}

/**
 * Days left in the trial window. Prefer the entitlement payload; when it is missing (entitlements
 * fetch failed before merge), derive from `freeTrialEnd` on the entity so the sidebar does not read
 * "0 days left" for an active trial.
 */
export function daysRemainingForSubscription(subscription?: Subscription | null): number {
  if (!subscription) {
    return 0
  }
  if (subscription.daysRemaining != null) {
    return subscription.daysRemaining
  }
  if (!subscription.freeTrialEnd) {
    return 0
  }
  const remainingMs = new Date(subscription.freeTrialEnd).getTime() - Date.now()
  if (remainingMs <= 0) {
    return 0
  }
  return Math.ceil(remainingMs / (24 * 60 * 60 * 1000))
}

/** The same fallback for a running trial: NOTCH_TRIAL, not locked, trial window still open. */
export function isActiveTrialSubscription(subscription?: Subscription | null): boolean {
  if (!subscription) {
    return false
  }
  // An explicit boolean came from the entitlement payload and is authoritative either way; only an
  // absent field is derived from the lifecycle columns.
  if (typeof subscription.isTrialActive === 'boolean') {
    return subscription.isTrialActive
  }
  if (subscription.lifecycleState !== LifecycleState.NOTCH_TRIAL || isSoftLockedSubscription(subscription)) {
    return false
  }
  return !!subscription.freeTrialEnd && new Date(subscription.freeTrialEnd).getTime() > Date.now()
}

/**
 * NS-205 cohort classification. Legacy (pre-ship) and new (post-ship) users share one codebase but
 * have different lock UX, so every trial/lock decision must branch on the cohort — never on the plan
 * name alone. The BE lifecycle fields are authoritative; the derived `isTrialActive` / `isTrialLocked`
 * booleans are also honoured so a BE that does not yet send `lifecycleState` still classifies right.
 */
export function isNotchTrialSubscription(subscription?: Subscription | null): boolean {
  if (!subscription) {
    return false
  }
  return (
    subscription.lifecycleState === LifecycleState.NOTCH_TRIAL ||
    subscription.accessState === AccessState.SOFT_LOCKED ||
    subscription.isTrialActive === true ||
    subscription.isTrialLocked === true
  )
}

/**
 * FREE without a Notch trial: legacy FREE teams and new teams whose trial was skipped
 * (email already claimed one, or trials disabled). This is the only cohort that keeps the
 * 3-day `team.createdAt` FreemiumEndModal hard lock.
 */
export function isFreeWithoutNotchTrialSubscription(subscription?: Subscription | null): boolean {
  if (!subscription || isNotchTrialSubscription(subscription)) {
    return false
  }
  const lifecycleState = subscription.lifecycleState ?? LifecycleState.NONE
  const accessState = subscription.accessState ?? AccessState.NORMAL
  return (
    subscription.plan === SubscriptionPlan.FREE &&
    lifecycleState === LifecycleState.NONE &&
    accessState === AccessState.NORMAL
  )
}

/** The legacy paid plan codes. SCALE has no catalog entry of its own and folds into GROWTH. */
const LEGACY_PAID_PLANS: string[] = [SubscriptionPlan.START, SubscriptionPlan.GROWTH, SubscriptionPlan.SCALE]

/**
 * On a legacy paid plan code alone, ignoring every trial flag.
 *
 * NS-203: the cohort helpers below all defer to `isNotchTrialSubscription` first, which is itself
 * derived from `isTrialActive` / `isTrialLocked` / `accessState`. That makes them useless as a
 * cross-check on those same flags — a legacy team wrongly marked `isTrialLocked` classifies as trial
 * cohort and stops being legacy. This reads the plan code only, so it still says "legacy paid" when
 * the trial flags disagree. A genuinely soft-locked team never has one of these codes: the BE swaps
 * the effective plan to TRIAL_EXPIRED.
 */
export function hasLegacyPaidPlanCode(subscription?: Subscription | null): boolean {
  return !!subscription && LEGACY_PAID_PLANS.includes(subscription.plan)
}

/**
 * Legacy paid plans: normal product, no trial banner / soft-lock bar / FreemiumEndModal.
 *
 * SCALE is included (NS-203): the BE keeps it on the plan enum and folds it into GROWTH's permission
 * set, so a SCALE team is legacy-paid in every way that matters here. Left out, it matched none of
 * the three cohorts and fell through to the trial-cohort defaults.
 */
export function isLegacyStartOrGrowthSubscription(subscription?: Subscription | null): boolean {
  if (!subscription || isNotchTrialSubscription(subscription)) {
    return false
  }
  return hasLegacyPaidPlanCode(subscription)
}

/**
 * Outcome of the last `/subscriptions/entitlements` read.
 *
 * `idle` and `failed` both leave `subscription.permissions` unset, but they must gate differently:
 * `idle` is a team loaded by a path that never asks for entitlements (the Salesforce widget's
 * `setCurrentTeam`), where the pre-NS-205 permissive behaviour is still correct, while `failed` means
 * the read was attempted and lost — the case that must fail closed. Inferring either from a missing
 * permission list alone cannot tell them apart.
 */
export type EntitlementsStatus = 'idle' | 'loaded' | 'failed'

/** One retry: an entitlements blip is usually transient, and denying on the first one hides paid features. */
const ENTITLEMENTS_RETRY_ATTEMPTS = 1
const ENTITLEMENTS_RETRY_DELAY_MS = 500

const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms))

export type TeamState = {
  currentTeam: Team | null
  entitlementsStatus: EntitlementsStatus
}

export const useTeamStore = defineStore('team', {
  state: (): TeamState => ({
    currentTeam: null,
    entitlementsStatus: 'idle'
  }),
  getters: {
    isTrialActive: state => isActiveTrialSubscription(state.currentTeam?.subscription),
    isTrialLocked: state => isSoftLockedSubscription(state.currentTeam?.subscription),
    daysRemaining: state => daysRemainingForSubscription(state.currentTeam?.subscription),
    warningLevel: state => state.currentTeam?.subscription?.warningLevel ?? null,
    subscriptionPermissions: state => state.currentTeam?.subscription?.permissions ?? [],
    isNotchTrialCohort: state => isNotchTrialSubscription(state.currentTeam?.subscription),
    isGrandfatherOrFreeNoTrial: state => isFreeWithoutNotchTrialSubscription(state.currentTeam?.subscription),
    isLegacyStartOrGrowth: state => isLegacyStartOrGrowthSubscription(state.currentTeam?.subscription),
    /** Legacy paid plan code, independent of the trial flags — see `hasLegacyPaidPlanCode`. */
    hasLegacyPaidPlan: state => hasLegacyPaidPlanCode(state.currentTeam?.subscription),
    /**
     * The entitlement read was attempted and failed, so the team's real permissions are unknown.
     * `FeatureRestrictionStore` fails closed on this rather than showing gated features that answer
     * with a 403 on click.
     */
    entitlementsUnavailable: state => state.entitlementsStatus === 'failed'
  },
  actions: {
    async fetchCurrentTeam(): Promise<Team> {
      // In parallel: the entitlement read is a second round trip on every boot and does not depend
      // on the team payload.
      const [teamResponse, entitlements] = await Promise.all([
        ApiClient.get<Team>(`/teams/current`),
        this.fetchEntitlements()
      ])
      const team = applyTeamApiNormalization(teamResponse.data)
      if (team.subscription && entitlements) {
        team.subscription = applyEntitlements(team.subscription, entitlements)
      }
      this.currentTeam = team
      ensureRolloutClearHook()
      syncStaticFirstRenderRollout(team, entitlements)
      return team
    },
    /**
     * Trial state and plan permissions. Still resolves to null rather than throwing — `initData` fans
     * this out with the essential calls, and losing entitlements must not fail app boot.
     *
     * NS-203: the failure is now *recorded* rather than swallowed. Returning null silently left
     * `permissions` unset, which `hasPermission` read as "allow everything": one entitlements 500 and
     * a BASIC team saw branding, content-library upload and create-list unlocked, then took a 403 on
     * click. Retry once first, because denying a paying customer their features over a transient blip
     * is its own failure.
     */
    async fetchEntitlements(): Promise<SubscriptionEntitlements | null> {
      for (let attempt = 0; ; attempt++) {
        try {
          const entitlements = (await ApiClient.get<SubscriptionEntitlements>('/subscriptions/entitlements')).data
          this.entitlementsStatus = 'loaded'
          return entitlements
        } catch (error) {
          if (attempt >= ENTITLEMENTS_RETRY_ATTEMPTS) {
            console.error('Failed to load subscription entitlements; gated features will be withheld.', error)
            this.entitlementsStatus = 'failed'
            return null
          }
          await delay(ENTITLEMENTS_RETRY_DELAY_MS)
        }
      }
    },
    async updateCurrentTeam(updatedTeam: Team): Promise<Team> {
      const team = applyTeamApiNormalization((await ApiClient.put<Team>(`/teams/current`, updatedTeam)).data)
      this.currentTeam = preserveEntitlements(team, this.currentTeam)
      return this.currentTeam
    },

    async uploadTeamLogo(file: File) {
      const formData = new FormData()
      formData.append('file', file)
      const response = await ApiClient({
        method: 'post',
        url: `/teams/current/teamlogo`,
        data: formData,
        timeout: FILE_TIMEOUT
      })
      const team = applyTeamApiNormalization(response.data as Team)
      this.currentTeam = preserveEntitlements(team, this.currentTeam)
      return this.currentTeam
    },
    async uploadTeamBanner(file: File) {
      const formData = new FormData()
      formData.append('file', file)
      const response = await ApiClient({
        method: 'post',
        url: `/teams/current/teambanner`,
        data: formData,
        timeout: FILE_TIMEOUT
      })
      const team = applyTeamApiNormalization(response.data as Team)
      this.currentTeam = preserveEntitlements(team, this.currentTeam)
      return this.currentTeam
    },
    async deleteTeamLogo() {
      const team = applyTeamApiNormalization((await ApiClient.delete<Team>(`/teams/current/teamlogo`)).data)
      this.currentTeam = preserveEntitlements(team, this.currentTeam)
      return this.currentTeam
    },
    async deleteTeamBanner() {
      const team = applyTeamApiNormalization((await ApiClient.delete<Team>(`/teams/current/teambanner`)).data)
      this.currentTeam = preserveEntitlements(team, this.currentTeam)
      return this.currentTeam
    },
    async getSubscriptionPortalLink(): Promise<string> {
      return (await ApiClient.get(`/teams/current/subscription-portal-link`)).data
    },
    async getCurrentCustomDomain(): Promise<WhitelistedDomain> {
      const { data } = await ApiClient.get<WhitelistedDomain>('/teams/current/domain')
      return data
    },
    async checkCurrentCustomDomain(currentDomain?: String): Promise<boolean> {
      const { data } = await ApiClient.get<boolean>('/teams/current/domain/check', { params: { currentDomain } })
      return data
    },
    async getCustomDomainStatus(hostName: String): Promise<DomainResponse> {
      const { data } = await ApiClient.get<DomainResponse>(`/teams/current/domain/availability/${hostName}`)
      return data
    },
    async createCertificate(hostName: String): Promise<DomainResponse> {
      const { data } = await ApiClient.post<DomainResponse>(`/teams/current/domain/certificate/${hostName}`)
      return data
    },
    async createDistribution(hostName: String): Promise<DomainResponse> {
      const { data } = await ApiClient.post<DomainResponse>(`/teams/current/domain/distribution/${hostName}`)
      return data
    },
    async deleteCustomDomain(hostName: string): Promise<DomainResponse> {
      const { data } = await ApiClient.delete(`/teams/current/domain/delete/${hostName}`)
      return data
    },
    async getEmailConfig(): Promise<TeamEmailConfig | null> {
      try {
        const { data } = await ApiClient.get<TeamEmailConfigGetResponse>('/teams/current/email-config')
        if (data.usingDefault || !data.config) {
          return null
        }
        return data.config
      } catch (error: any) {
        if (error?.response?.status === 404) {
          return null
        }
        throw error
      }
    },
    async saveEmailConfig(payload: TeamEmailConfigRequest): Promise<TeamEmailConfigSaveResponse> {
      const { data } = await ApiClient.post<TeamEmailConfigSaveResponse>('/teams/current/email-config', payload)
      if (!data) {
        throw new Error('Invalid email-config save response')
      }
      // data.config is legitimately null when a failed SMTP handshake reverted the team to the
      // default provider (usingDefault=true) — that's a valid outcome, not an error.
      return data
    },
    async deleteEmailConfig(): Promise<void> {
      await ApiClient.delete('/teams/current/email-config')
    },
    setCurrentTeam(team: Team) {
      // Widget / CRM loads swap teams without an entitlements read — do not inherit a prior failure.
      this.entitlementsStatus = 'idle'
      this.currentTeam = applyTeamApiNormalization(team)
    }
  }
})

export type TeamStore = ReturnType<typeof useTeamStore>
